import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { EditAction, RejectedWithValueAction } from '../../types/common';
import type { CreateOrganizationParams, DeleteOrganizationParams, OrganizationState, OrganizationType, UpdateOrganizationParamsClient } from '../../types/organization';
import { GENERAL_ERROR_MESSAGE } from '../../constants';
import organizationAPI from '../../api/organizationAPI';
import getAsyncErrorMessage from '../../utils/getAsyncErrorMessage';

export const initialState: OrganizationState = {
  isLoading: false,
  hasError: false,
  serverMessage: undefined,
  dialogMode: undefined,
  isDialogOpen: false,
  organization: undefined,
  organizations: undefined,
};

const slice = createSlice({
  name: 'Organization',
  initialState,
  reducers: {
    startLoading(state: OrganizationState) {
      state.isLoading = true;
    },

    hasError(state: OrganizationState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: OrganizationState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: OrganizationState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    setDialogMode(state: OrganizationState, action: PayloadAction<string>): void {
      state.dialogMode = action.payload;
    },

    openDialog(state: OrganizationState): void {
      state.isDialogOpen = true;
    },

    closeDialog(state: OrganizationState): void {
      state.isDialogOpen = false;
      state.dialogMode = undefined;
      state.organization = undefined;
    },

    selectOrganization(state: OrganizationState, action: PayloadAction<OrganizationType | undefined>): void {
      if (action.payload !== undefined) {
        const organization: OrganizationType = action.payload;
        const selectedOrganization: OrganizationType | undefined = state.organizations.find((org) => org.id === organization.id);
        if (selectedOrganization !== undefined) {
          state.organization = selectedOrganization;
        } else {
          state.organization = action.payload;
        }
      } else {
        state.organization = undefined;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      // Get Organizations
      // ----------------------------------------
      .addCase(getOrganizations.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOrganizations.fulfilled, (state, action) => {
        state.isLoading = false;
        // ソートはサーバ側
        state.organizations = action.payload;
      })
      .addCase(getOrganizations.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<undefined, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organizations = [];
      })
      // Create Organizations
      // ----------------------------------------
      .addCase(createOrganization.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        state.organization = undefined;
        state.organizations = state.organizations === undefined ? [action.payload] : [...state.organizations, action.payload];
      })
      .addCase(createOrganization.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<CreateOrganizationParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organization = undefined;
        state.organizations = state.organizations === undefined ? [] : state.organizations;
      })
      // Update Organizations
      // ----------------------------------------
      .addCase(updateOrganization.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(updateOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedOrganization = action.payload;
        state.organization = undefined;
        state.organizations = state.organizations.map((organization) => {
          if (organization.id === updatedOrganization.id) {
            return updatedOrganization;
          } else {
            return organization;
          }
        });
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<UpdateOrganizationParamsClient, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organization = undefined;
        state.organizations = state.organizations === undefined ? [] : state.organizations;
      })
      // Delete Organizations
      // ----------------------------------------
      .addCase(deleteOrganization.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.isLoading = false;
        const deletedOrganization = action.payload;
        state.organization = undefined;
        state.organizations = state.organizations.filter((organization) => {
          if (organization.id === deletedOrganization.id) {
            return false;
          } else {
            return true;
          }
        });
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        // console.log(action);
        // actionはwithValueでpayloadがある
        const rejectedWithValueAction = action as RejectedWithValueAction<DeleteOrganizationParams, string>;
        const message: string = rejectedWithValueAction.payload !== '' ? rejectedWithValueAction.payload : GENERAL_ERROR_MESSAGE;
        state.isLoading = false;
        state.hasError = true;
        state.serverMessage = message;
        state.organization = undefined;
        state.organizations = state.organizations === undefined ? [] : state.organizations;
      });
  },
});

export const { reducer, actions } = slice;

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const setDialogMode =
  (mode?: EditAction): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.setDialogMode(mode));
  };

export const openDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.openDialog());
  };

export const closeDialog =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.closeDialog());
  };

export const selectOrganization =
  (organization?: OrganizationType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectOrganization(organization));
  };

export const getOrganizations = createAsyncThunk('Organization/getOrganizations', async (_, thunkAPI) => {
  try {
    const response = await organizationAPI.getOrganizations();
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const createOrganization = createAsyncThunk('Organization/createOrganization', async (params: CreateOrganizationParams, thunkAPI) => {
  try {
    const response = await organizationAPI.createOrganization(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const updateOrganization = createAsyncThunk('Organization/updateOrganization', async (params: UpdateOrganizationParamsClient, thunkAPI) => {
  try {
    const response = await organizationAPI.updateOrganization(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export const deleteOrganization = createAsyncThunk('Organization/deleteOrganization', async (params: DeleteOrganizationParams, thunkAPI) => {
  try {
    const response = await organizationAPI.deleteOrganization(params);
    return response.data;
  } catch (err) {
    const message: string = getAsyncErrorMessage(err);
    return thunkAPI.rejectWithValue(message);
  }
});

export default slice;
